import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  OpenInNew as DetailIcon,
  FlashOn as QuickReceiveIcon,
  DeleteSweep as WastageIcon,
  Dashboard as DashboardIcon,
  Tune as AdjustIcon,
  NotificationsNone as AlertsIcon,
  Upload as ImportIcon,
  Category as CategoryIcon,
  SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import inventoryItemService, {
  extractInventoryError, extractInventoryErrorDetail,
} from '../../../../services/inventoryItemService';
import ItemRowRetireAction from '../../../../components/venue/ItemRowRetireAction';
import { STATUS_TONES } from '../../../../components/venue/itemStatusTones';
import RetireSweepBanner from '../../../../components/venue/RetireSweepBanner';
import ItemDeactivationBlockedDialog from '../../../../components/venue/ItemDeactivationBlockedDialog';
import useRetireUndo from '../../../../components/venue/useRetireUndo';
import inventoryCategoryService from '../../../../services/inventoryCategoryService';
import venueLocationService from '../../../../services/venueLocationService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import QuickReceiveModal from '../../../../components/venue/QuickReceiveModal';
import RecordWastageModal from '../../../../components/venue/RecordWastageModal';
import InventoryImportDialog from '../../../../components/venue/InventoryImportDialog';
import InventoryExportMenu from '../../../../components/venue/InventoryExportMenu';
import InventoryModuleHelpModal from '../../../../components/venue/InventoryModuleHelpModal';
import type { BulkActiveResult, InventoryItemWithBalance, MissingDimension, StockStatus } from '../../../../services/inventoryItemService';
import { relativeDate } from '../../../../utils/dateUtils';
import type { InventoryCategory, InventoryItemType, VenueLocation } from '../../../../types/venue';
import { fromMinorUnits } from '../../../../utils/money';
import { humanizeItemType, itemTypeColor, ITEM_TYPE_FILTER_OPTIONS } from '../../../../utils/inventoryItemDisplay';
import ColumnVisibilityMenu from '../../../../components/venue/ColumnVisibilityMenu';
import { useColumnVisibility } from '../../../../utils/columnVisibility';
import type { ColumnDef } from '../../../../utils/columnVisibility';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ── Column model (INVENTORY-LIST-COLUMNS.1) ─────────────────────────────────────
// Toggleable, per-venue-persisted columns for the inventory list. `money: true`
// columns (Sale Price / Last cost / Value on hand) are gated by stock-costs:view
// — hidden from the table AND the selector for unentitled users; the server also
// strips their values, which is the real boundary. `defaultVisible` mirrors the
// pre-columns list (Item, Total Stock, Status, Par, Last cost, Supplier, Source,
// actions) so nothing regresses. `item` and `actions` are locked (always shown).
type InvColMeta = ColumnDef & {
  width: number;
  align?: 'right';
  money?: boolean;
  defaultVisible: boolean;
};

const INVENTORY_COLUMNS: InvColMeta[] = [
  { key: 'item',              label: 'Item',           locked: true, width: 300, defaultVisible: true },
  { key: 'sku',               label: 'SKU',            width: 108, defaultVisible: false },
  { key: 'type',              label: 'Item type',      width: 116, defaultVisible: false },
  { key: 'category',          label: 'Category',       width: 140, defaultVisible: false },
  { key: 'subcategory',       label: 'Subcategory',    width: 140, defaultVisible: false },
  { key: 'size',              label: 'Size',           width: 92,  defaultVisible: false },
  { key: 'totalStock',        label: 'Total Stock',    width: 104, defaultVisible: true },
  { key: 'stockStatus',       label: 'Status',         width: 76,  defaultVisible: true },
  { key: 'reserved',          label: 'Reserved',       width: 92,  align: 'right', defaultVisible: false },
  { key: 'available',         label: 'Available',      width: 92,  align: 'right', defaultVisible: false },
  { key: 'par',               label: 'Par',            width: 60,  align: 'right', defaultVisible: true },
  { key: 'reorderPoint',      label: 'Reorder point',  width: 108, align: 'right', defaultVisible: false },
  { key: 'supplier',          label: 'Supplier',       width: 150, defaultVisible: true },
  { key: 'lastReceived',      label: 'Last received',  width: 120, defaultVisible: false },
  { key: 'binLocation',       label: 'Bin location',   width: 112, defaultVisible: false },
  { key: 'lotTracked',        label: 'Lot tracked',    width: 96,  defaultVisible: false },
  { key: 'source',            label: 'Source',         width: 110, defaultVisible: true },
  { key: 'executionBehavior', label: 'Sold from',      width: 128, defaultVisible: false },
  { key: 'active',            label: 'Item status',    width: 100, defaultVisible: false },
  { key: 'salePrice',         label: 'Sale Price',     width: 108, align: 'right', money: true, defaultVisible: false },
  { key: 'lastCost',          label: 'Last cost',      width: 104, align: 'right', money: true, defaultVisible: true },
  { key: 'costPerServing',    label: 'Cost / serving', width: 122, align: 'right', money: true, defaultVisible: false },
  { key: 'valueOnHand',       label: 'Value on hand',  width: 120, align: 'right', money: true, defaultVisible: false },
  { key: 'actions',           label: '',               locked: true, width: 88, defaultVisible: true },
];

const INVENTORY_COLUMN_DEFAULTS: Record<string, boolean> = INVENTORY_COLUMNS.reduce(
  (acc, c) => { acc[c.key] = c.defaultVisible; return acc; },
  {} as Record<string, boolean>,
);

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive stock status from local data.
 * Used for display while the backend Phase 10 status field is not yet available,
 * and as a fallback if the backend omits it.
 */
function deriveStatus(item: InventoryItemWithBalance): StockStatus | null {
  // Prefer backend-derived value once available
  if (item.stockStatus !== undefined) return item.stockStatus ?? null;

  // RECIPE-SERVINGS.1 — never derive a status for a recipe item from its own
  // balance. It has none, so the fallback below would read 0 and answer "Out"
  // for every recipe ever created. Its status is what its components can make,
  // which only the backend can compute.
  if (item.executionBehavior === 'RECIPE_CONSUMPTION') return null;

  const qty = item.balance?.totalQuantity;
  if (qty == null) return null;
  if (qty <= 0) return 'OUT';
  if (item.reorderPoint != null && qty <= item.reorderPoint) return 'LOW';
  if (item.reorderPoint != null) return 'OK';
  return null; // no reorderPoint configured
}

/** Formats supplier for a compact list cell. Shows last received supplier + date if available,
 *  falls back to preferred supplier link name, then dash. */
function fmtSupplier(
  lastSupplierName: string | undefined,
  date: string | undefined,
  preferredSupplierName: string | null | undefined,
): string {
  const name = lastSupplierName || preferredSupplierName;
  if (name && date) return `${name} · ${relativeDate(date)}`;
  if (name) return name;
  if (date) return relativeDate(date);
  return '—';
}

// ── Stock status badge ────────────────────────────────────────────────────────

// ITEM-ACTIVE.4b — the colours moved to itemStatusTones so the lifecycle chip
// can speak the same dialect from the item header. The labels stay here; they
// belong to the stock statuses, which are this list's business.
const STATUS_CONFIG: Record<StockStatus, { label: string; color: string; bg: string }> = {
  OUT: { label: 'Out', ...STATUS_TONES.OUT },
  LOW: { label: 'Low', ...STATUS_TONES.LOW },
  OK:  { label: 'OK', ...STATUS_TONES.OK },
  RESERVED: { label: 'Reserved', ...STATUS_TONES.RESERVED },
};

function StockStatusBadge({ status }: { status: StockStatus | null }) {
  if (!status) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }
  const cfg = STATUS_CONFIG[status];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: 'none',
        height: 22,
        minWidth: 36,
      }}
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryItemList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { venueId, defaultCurrency } = useVenue();
  const { isAdmin, canViewStockCosts } = useAuth();

  // INVENTORY-LIST-COLUMNS.1 — entitlement mirrors the server strip: money
  // columns are hidden from the table and the selector when the user cannot
  // view stock costs. The server nulling their values is the real boundary.
  const showCost = canViewStockCosts();
  const { visible, toggle, reset } = useColumnVisibility(
    'inventoryItems.cols', venueId, INVENTORY_COLUMNS, INVENTORY_COLUMN_DEFAULTS,
  );
  const activeColumns = useMemo(
    () => INVENTORY_COLUMNS.filter(c => (showCost || !c.money) && (c.locked || visible[c.key])),
    [visible, showCost],
  );
  // The selector omits the actions column and any money column the user can't see.
  const menuColumns = useMemo(
    () => INVENTORY_COLUMNS.filter(c => c.key !== 'actions' && (showCost || !c.money)),
    [showCost],
  );
  const tableMinWidth = activeColumns.reduce((sum, c) => sum + c.width, 0);

  // ── Filters — initialise from URL query params so dashboard cards can link in ─
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockStatus | ''>(() => {
    const s = searchParams.get('status');
    return (s === 'LOW' || s === 'OUT' || s === 'OK') ? s : '';
  });
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('active');
  /**
   * ITEM-GAPS.1 — the accountant's worklist: items whose supplier, cost or sale
   * price is blank. Deep-linkable, so a dashboard card or a saved link can drop
   * someone straight into the work.
   */
  const [missingFilter, setMissingFilter] = useState<MissingDimension | ''>(() => {
    const m = searchParams.get('missing');
    return m === 'supplier' || m === 'cost' || m === 'price' || m === 'any' ? m : '';
  });
  /**
   * ITEM-MASTER — 'venue' is what this venue stocks; 'company' is everything
   * its company defines, including products a sister venue set up that this one
   * has not adopted. Browsing is the alternative to discovering them by trying
   * to create a duplicate and reading the 409.
   */
  const [catalogueScope, setCatalogueScope] = useState<'venue' | 'company'>('venue');
  const [addingId, setAddingId] = useState<string | null>(null);

  /**
   * ITEM-ACTIVE.4 — the selection mode is gone.
   *
   * Ticking boxes and then hunting upward for a toolbar was two context
   * switches for one small decision. The act now lives at the end of its own
   * row, and the many-item case is a reviewed sweep rather than a mode.
   */
  const [flipBusy, setFlipBusy] = useState(false);
  const [blockedItem, setBlockedItem] = useState<InventoryItemWithBalance | null>(null);

  // Deep-linked ?type= must accept every filterable type (incl. RECIPE).
  const VALID_TYPES = new Set<string>(ITEM_TYPE_FILTER_OPTIONS);

  const [typeFilter, setTypeFilter] = useState<InventoryItemType | ''>(() => {
    const t = searchParams.get('type');
    return t && VALID_TYPES.has(t) ? t as InventoryItemType : '';
  });

  // ── Quick Receive ─────────────────────────────────────────────────────────────
  const [helpOpen, setHelpOpen] = useState(false);
  const [quickReceiveOpen, setQuickReceiveOpen] = useState(false);
  // WASTAGE.1 — stock gone but not sold, recorded with a reason at the moment
  // someone knows it, rather than surfacing weeks later as an unexplained count
  // variance.
  const [wastageOpen, setWastageOpen] = useState(false);

  // ── Import ────────────────────────────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<InventoryItemWithBalance[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0); // MUI is 0-based; API is 1-based
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [locations, setLocations] = useState<VenueLocation[]>([]);

  // ── Dynamic (user-definable) category filter ──────────────────────────────────
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [categoryIdFilter, setCategoryIdFilter] = useState<string>(() => searchParams.get('categoryId') ?? '');
  /** Which gap dimensions the SERVER actually ran (see loadItems). */
  const [appliedMissing, setAppliedMissing] = useState<string[] | null>(null);
  const [exporting, setExporting] = useState(false);

  // ── Load locations once ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Load active categories once (for the dynamic category filter) ─────────────
  useEffect(() => {
    if (!venueId) return;
    inventoryCategoryService.getCategories(venueId, {})
      .then(setCategories)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Debounced search ──────────────────────────────────────────────────────────
  const applySearch = useMemo(
    () => debounce((q: string) => { setSearchQuery(q); setPage(0); }, 350),
    [],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    applySearch(e.target.value);
  };

  // ── Load items ────────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryItemService.getItems(
        venueId,
        page + 1,
        pageSize,
        'displayName',
        'asc',
        searchQuery || undefined,
        locationFilter || undefined,
        statusFilter || undefined,
        activeFilter,
        typeFilter || undefined,
        categoryIdFilter || undefined,
        undefined,
        catalogueScope,
        missingFilter || undefined,
      );
      setItems(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
      // The server drops the cost dimension for a caller who cannot see cost.
      // Surfaced rather than swallowed: otherwise an empty result reads as
      // "no cost gaps" when cost was never looked at.
      setAppliedMissing((result as { missingFilter?: { applied: string[] } }).missingFilter?.applied ?? null);
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, searchQuery, locationFilter, statusFilter, activeFilter, typeFilter, categoryIdFilter, catalogueScope, missingFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  /**
   * ITEM-GAPS.1 — export what is on screen, filters and all.
   *
   * The gap-filling job is done in a spreadsheet: the accountant wants the
   * whole filtered set at once, not 50 rows at a time. The list endpoint caps a
   * page at 200, so this walks the pages rather than asking for one huge one.
   *
   * Cost columns are omitted entirely for a caller without stock-costs:view —
   * the server already nulls them, and a column of blanks would read as "no
   * costs recorded" rather than "not yours to see".
   */
  const exportCsv = async () => {
    if (!venueId) return;
    setExporting(true);
    setError(null);
    try {
      const PAGE = 200;
      const MAX_PAGES = 60; // 12,000 rows — far past any real catalogue.
      const rows: InventoryItemWithBalance[] = [];
      for (let p = 1; p <= MAX_PAGES; p += 1) {
        const res = await inventoryItemService.getItems(
          venueId, p, PAGE, 'displayName', 'asc',
          searchQuery || undefined, locationFilter || undefined,
          statusFilter || undefined, activeFilter, typeFilter || undefined,
          categoryIdFilter || undefined, undefined, catalogueScope,
          missingFilter || undefined,
        );
        rows.push(...(res.data ?? []));
        const total = res.pagination?.totalItems ?? rows.length;
        if (rows.length >= total || (res.data?.length ?? 0) === 0) break;
      }

      const headers = [
        'Short ID', 'Item', 'Type', 'Category', 'Supplier',
        ...(showCost ? ['Last cost'] : []),
        'Sale price', 'Currency', 'Stock on hand', 'Status',
      ];
      const body = rows.map(item => [
        item.shortId ?? '',
        item.displayName ?? '',
        item.type ?? '',
        item.categoryName ?? '',
        // The same rule the Supplier COLUMN uses, minus the relative date —
        // a date is a reading aid on screen and noise in a spreadsheet.
        item.lastSupplierName || item.preferredSupplierName || '',
        ...(showCost ? [item.balance?.lastCost != null ? (item.balance.lastCost / 100).toFixed(2) : ''] : []),
        item.salePrice != null ? (item.salePrice / 100).toFixed(2) : '',
        item.salePriceCurrency ?? defaultCurrency ?? '',
        String(item.balance?.totalQuantity ?? 0),
        deriveStatus(item) ?? '',
      ]);

      // Quote every field and double any embedded quote: item names carry
      // commas ("Château Margaux, Magnum") and the occasional inch mark.
      const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
      const csv = [headers, ...body].map(r => r.map(esc).join(',')).join('\r\n');

      const stamp = new Date().toISOString().slice(0, 10);
      const name = missingFilter
        ? `inventory-items-missing-${missingFilter}-${stamp}.csv`
        : `inventory-items-${stamp}.csv`;
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setExporting(false);
    }
  };

  /**
   * Adopt a company product into this venue — one overlay row, not a second
   * master. Duplicating the master is what the company identity index exists to
   * prevent, and what the 409 on create is telling the user about.
   */
  const handleAddToVenue = async (itemId: string) => {
    if (!venueId) return;
    setAddingId(itemId);
    try {
      await inventoryItemService.setVenueSettings(venueId, itemId, { active: true });
      await loadItems();
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setAddingId(null);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const itemBase = (itemId: string) => `/admin/venues/${venueId}/inventory/${itemId}`;

  // ── Per-column cell rendering ──────────────────────────────────────────────────
  const moneyCell = (minor: number | null | undefined): React.ReactNode =>
    minor != null ? (
      <Box>
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'text.disabled', lineHeight: 1.2 }}>
          {defaultCurrency ?? ' '}
        </Typography>
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.2 }}>
          {fromMinorUnits(minor).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Box>
    ) : (
      <Typography variant="caption" color="text.disabled">—</Typography>
    );

  const textCell = (v: React.ReactNode): React.ReactNode => (
    <Typography fontSize="0.78rem" color="text.secondary" noWrap>{v == null || v === '' ? '—' : v}</Typography>
  );
  const numCell = (v: number | null | undefined): React.ReactNode => (
    <Typography fontSize="0.78rem" color="text.secondary">{v != null ? v : '—'}</Typography>
  );

  function renderCell(col: InvColMeta, item: InventoryItemWithBalance): React.ReactNode {
    switch (col.key) {
      case 'item':
        return (
          <>
            <Typography
              fontSize="0.83rem"
              fontWeight={500}
              sx={{ lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {item.displayName}
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* ITEM-MASTER — only meaningful while browsing the company
                  catalogue, where the list deliberately includes products this
                  venue has not adopted. Adopting writes one overlay row; it
                  does NOT copy the master. */}
              {catalogueScope === 'company' && item.stockedHere === false && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={addingId === item._id}
                  onClick={e => { e.stopPropagation(); handleAddToVenue(item._id); }}
                  sx={{ fontSize: '0.63rem', height: 20, py: 0, px: 1, minWidth: 0 }}
                >
                  {addingId === item._id ? 'Adding…' : 'Add to venue'}
                </Button>
              )}
              {item.executionBehavior === 'RECIPE_CONSUMPTION' ? (
                <Chip
                  label="Recipe"
                  size="small"
                  sx={{ fontSize: '0.63rem', height: 18, fontWeight: 600, color: '#9c27b0', backgroundColor: alpha('#9c27b0', 0.08), border: 'none' }}
                />
              ) : (
                <Chip
                  label={humanizeItemType(item.type)}
                  size="small"
                  sx={{
                    fontSize: '0.63rem', height: 18, fontWeight: 600,
                    color: itemTypeColor(item.type) ?? theme.palette.text.secondary,
                    backgroundColor: alpha(itemTypeColor(item.type) ?? theme.palette.text.secondary, 0.08),
                    border: 'none',
                  }}
                />
              )}
            </Box>
          </>
        );
      case 'sku':
        return textCell(item.shortId ?? '—');
      case 'type':
        return textCell(humanizeItemType(item.type));
      case 'category':
        return textCell(item.categoryName);
      case 'subcategory':
        return textCell(item.subcategoryName);
      case 'size':
        return textCell(item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : '—');
      case 'totalStock':
        // RECIPE-SERVINGS.1 — a recipe item holds no balance of its own, so this
        // column showed "—" and Status showed "Out" while its components sat on
        // the shelf. What it can MAKE is the honest figure, and it is derived
        // rather than counted — rendered plainly, not as a stock chip, so it is
        // never mistaken for something a count would reconcile against.
        return item.executionBehavior === 'RECIPE_CONSUMPTION'
          ? (item.buildableServings != null ? (
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
                <Typography variant="body2" fontWeight={700}>{item.buildableServings}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                  can make
                </Typography>
              </Box>
            ) : (
              // No recipe authored yet — unfinished setup, not out of stock.
              <Typography variant="caption" color="text.disabled">No recipe</Typography>
            ))
          : item.balance != null ? (
            <InventoryQuantityBadge quantity={item.balance.totalQuantity} parLevel={item.parLevel} reorderPoint={item.reorderPoint} reserved={item.reserved} />
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
      case 'stockStatus':
        return <StockStatusBadge status={deriveStatus(item)} />;
      case 'reserved':
        return numCell(item.reserved);
      case 'available':
        return numCell(item.available);
      case 'par':
        return numCell(item.parLevel);
      case 'reorderPoint':
        return numCell(item.reorderPoint);
      case 'supplier':
        return textCell(fmtSupplier(item.lastSupplierName, item.lastReceivedDate, item.preferredSupplierName));
      case 'lastReceived':
        return textCell(item.lastReceivedDate ? relativeDate(item.lastReceivedDate) : '—');
      case 'binLocation':
        return textCell(item.binLocation);
      case 'lotTracked':
        return textCell(item.lotTracked ? 'Yes' : 'No');
      case 'source':
        return (
          <Chip label={item.source} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20, opacity: 0.7, maxWidth: '100%' }} />
        );
      case 'executionBehavior':
        return textCell(item.executionBehavior === 'RECIPE_CONSUMPTION' ? 'Recipe' : 'Direct stock');
      case 'active':
        return textCell(item.active ? 'Active' : 'Inactive');
      case 'salePrice':
        return moneyCell(item.salePrice ?? null);
      case 'lastCost':
        return moneyCell(item.balance?.lastCost ?? null);
      case 'costPerServing':
        // RECIPE-COST-COLUMN.1 — recipe rows only. Blank elsewhere rather than a
        // dash: a bottle does not have a "per serving" cost that is merely
        // unknown, the question does not apply to it.
        if (item.executionBehavior !== 'RECIPE_CONSUMPTION') return null;
        return item.costPerServing != null ? (
          <Tooltip
            title={item.costComplete === false
              ? 'Some ingredients have no recorded cost — this is a minimum, not the full cost.'
              : 'Cost of one serving, from current ingredient costs. Moves when an ingredient\'s cost moves.'}
            placement="top"
          >
            <Box sx={{ display: 'inline-block', ...(item.costComplete === false && { opacity: 0.75 }) }}>
              {moneyCell(item.costPerServing)}
              {item.costComplete === false && (
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6875rem', color: '#9a6700', textAlign: 'right' }}>
                  partial
                </Typography>
              )}
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        );
      case 'valueOnHand':
        return moneyCell(item.valueOnHand ?? null);
      case 'actions':
        return (
          <>
            <Tooltip title="Adjust stock">
              <IconButton
                size="small"
                onClick={() => navigate(`/admin/venues/${venueId}/inventory/adjust?itemId=${item._id}`)}
                sx={{ color: theme.palette.text.secondary }}
              >
                <AdjustIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View detail">
              <IconButton
                size="small"
                onClick={() => navigate(itemBase(item._id))}
                sx={{ color: theme.palette.text.secondary }}
              >
                <DetailIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        );
      default:
        return null;
    }
  }


  // ── Empty state message ───────────────────────────────────────────────────────
  /**
   * Undo re-flips through the API and waits for the answer — an Undo that only
   * hid the toast would be a lie told at the moment somebody realised their
   * mistake. See useRetireUndo.
   */
  const { offer: offerUndo, toast: undoToast } = useRetireUndo({
    venueId,
    onUndone: loadItems,
    onError: setError,
  });

  // ── ITEM-ACTIVE.4 — flipping, from the row or from the sweep ──────────────
  const qtyOf = (item: InventoryItemWithBalance) => item.balance?.totalQuantity ?? 0;

  /** One row, one tap. Instant, with a real way back. */
  const flipOne = async (item: InventoryItemWithBalance, next: boolean) => {
    if (!venueId) return;
    setFlipBusy(true);
    setError(null);
    try {
      await inventoryItemService.setActive(venueId, item._id, next);
      await loadItems();
      offerUndo({
        message: `${next ? 'Reactivated' : 'Retired'} ${item.displayName ?? 'item'}`,
        itemIds: [item._id],
        restoreTo: !next,
      });
    } catch (err) {
      // The stock guard answers here as it does everywhere else: with the
      // dialog that says what to do about it, not a red line.
      const detail = extractInventoryErrorDetail(err);
      if (detail.code === 'ITEM_HAS_STOCK') setBlockedItem(item);
      else setError(detail.message);
    } finally {
      setFlipBusy(false);
    }
  };

  /** The sweep: one call, and the server's per-item verdict comes back. */
  const applySweep = async (itemIds: string[]): Promise<BulkActiveResult | null> => {
    if (!venueId || itemIds.length === 0) return null;
    setFlipBusy(true);
    setError(null);
    try {
      const result = await inventoryItemService.bulkSetActive(venueId, itemIds, false);
      await loadItems();
      if (result.updated.length > 0) {
        offerUndo({
          message: `Retired ${result.updated.length} item${result.updated.length === 1 ? '' : 's'}`,
          itemIds: result.updated,
          restoreTo: true,
        });
      }
      return result;
    } catch (err) {
      setError(extractInventoryError(err));
      return null;
    } finally {
      setFlipBusy(false);
    }
  };

  const emptyMessage = (): string => {
    if (statusFilter === 'LOW') {
      return 'No low-stock items found. Set reorder points on inventory items to enable low-stock alerts.';
    }
    if (statusFilter === 'OUT') {
      return 'No out-of-stock items. All tracked items have stock.';
    }
    if (statusFilter === 'RESERVED') {
      return 'View currently reserved items on the Inventory Dashboard (Currently reserved card).';
    }
    if (activeFilter === 'inactive') {
      return 'No inactive items found.';
    }
    if (searchQuery || locationFilter || statusFilter) {
      return 'No items match the current filters.';
    }
    return 'No inventory items yet. Use Add Item to start tracking stock.';
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <PageHeader
        icon={<InventoryIcon />}
        title="Inventory Items"
        count={!loading && totalItems > 0 ? totalItems : undefined}
        onHelp={() => setHelpOpen(true)}
        helpTitle="How this module works"
        actions={
          <>
          <ColumnVisibilityMenu
            columns={menuColumns}
            visible={visible}
            onToggle={toggle}
            onReset={reset}
            disabled={loading}
          />
          <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/dashboard`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<AlertsIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/alerts`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Alerts
          </Button>
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/categories`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Categories
          </Button>
          {/* COSTING.1a-web (E6) — ADMIN-only settings status page; gate the link
              so VENUE_USER doesn't reach a route FullAdminGuard would bounce. */}
          {isAdmin() && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate(`/admin/venues/${venueId}/inventory/settings`)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Settings
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<QuickReceiveIcon />}
            onClick={() => setQuickReceiveOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Quick Receive
          </Button>
          <Button
            variant="outlined"
            startIcon={<WastageIcon />}
            onClick={() => setWastageOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Wastage
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory/create`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Add Item
          </Button>
          </>
        }
      />

      {/* Filters */}
      <FilterBar
        actions={
          <InventoryExportMenu
            venueId={venueId}
            onExportView={exportCsv}
            exportingView={exporting}
            disabled={loading}
          />
        }
      >
        <TextField
          size="small"
          label="Search items"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ minWidth: 260, flex: 1 }}
        />
        {/* ITEM-GAPS.1 — filter to the blanks. Each option tests exactly what
            its column renders, so the loop of filter → fix → re-filter ends
            with an empty list rather than rows that still look blank. */}
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Needs attention</InputLabel>
          <Select
            value={missingFilter}
            label="Needs attention"
            onChange={e => { setMissingFilter(e.target.value as MissingDimension | ''); setPage(0); }}
          >
            {/* An ARRAY, not conditional JSX: a `false` among a Select's
                children makes MUI's Menu throw on a Fragment child. */}
            {[
              <MenuItem key="none" value="">No gap filter</MenuItem>,
              <MenuItem key="any" value="any">Any missing value</MenuItem>,
              <MenuItem key="supplier" value="supplier">No supplier</MenuItem>,
              // Hidden for a caller without stock-costs:view, mirroring the
              // money COLUMNS — the server would 403 it anyway.
              ...(showCost ? [<MenuItem key="cost" value="cost">No cost</MenuItem>] : []),
              <MenuItem key="price" value="price">No sale price</MenuItem>,
            ]}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={locationFilter}
            label="Location"
            onChange={e => { setLocationFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.displayName}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Stock status</InputLabel>
          <Select
            value={statusFilter}
            label="Stock status"
            onChange={e => { setStatusFilter(e.target.value as StockStatus | ''); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="LOW">Low stock</MenuItem>
            <MenuItem value="OUT">Out of stock</MenuItem>
            <MenuItem value="RESERVED">Reserved items</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Items</InputLabel>
          <Select
            value={activeFilter}
            label="Items"
            onChange={e => { setActiveFilter(e.target.value as 'active' | 'inactive' | 'all'); setPage(0); }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="all">All items</MenuItem>
          </Select>
        </FormControl>
        {/* ITEM-MASTER — the company catalogue is a SCOPE, not a filter: it
            changes which products are in view, not which of them are shown. */}
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Catalogue</InputLabel>
          <Select
            value={catalogueScope}
            label="Catalogue"
            onChange={e => { setCatalogueScope(e.target.value as 'venue' | 'company'); setPage(0); }}
          >
            <MenuItem value="venue">This venue</MenuItem>
            <MenuItem value="company">All company products</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={e => { setTypeFilter(e.target.value as InventoryItemType | ''); setPage(0); }}
          >
            <MenuItem value="">All types</MenuItem>
            {ITEM_TYPE_FILTER_OPTIONS.map(t => (
              <MenuItem key={t} value={t}>{humanizeItemType(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Dynamic (user-definable) category filter — sends categoryId. */}
        {categories.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryIdFilter}
              label="Category"
              onChange={e => { setCategoryIdFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
              <MenuItem value="none">— Unassigned —</MenuItem>
            </Select>
          </FormControl>
        )}
      </FilterBar>

      {/* The server drops the cost dimension for a caller who cannot see cost.
          Without this line an empty result reads as "nothing is missing a
          cost", when cost was never one of the things looked at. */}
      {missingFilter === 'any' && appliedMissing != null && !appliedMissing.includes('cost') && (
        <Alert severity="info">
          Showing missing suppliers and sale prices. Cost is not included — you do not have permission to view stock costs.
        </Alert>
      )}

      {/* Error */}
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {/* ITEM-ACTIVE.4 — "retire the ones that are out of stock", as ONE
          decision. Replaces the selection mode: nothing to tick, no toolbar
          to hunt for, and nothing commits until the review is applied. */}
      {activeFilter === 'active' && (
        <RetireSweepBanner
          candidates={items.filter(i => i.active !== false && qtyOf(i) === 0)}
          blocked={items.filter(i => i.active !== false && qtyOf(i) !== 0 && i.stockStatus === 'OUT')}
          canEdit={isAdmin()}
          busy={flipBusy}
          onApply={applySweep}
        />
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* UI-CONSISTENCY.2 — a plain table inside one Paper, like every other
            list in the venue area. This page used a card-table: rows separated
            by borderSpacing, each drawing its own border and rounded end caps,
            so it read as a stack of cards next to tables that read as tables.
            The fixed layout and colgroup stay — those drive the column-width
            system, not the look. */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
            <colgroup>
              {activeColumns.map(col => (
                <col key={col.key} style={{ width: col.width }} />
              ))}
              {/* ITEM-ACTIVE.4 — the act lives at the END of the row now. */}
              <col style={{ width: 92 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {activeColumns.map(col => (
                  // Typography and the header rule come from the theme. The
                  // borderless override here existed only because the card-table
                  // rows drew their own borders.
                  <TableCell key={col.key} align={col.align}>
                    {col.label}
                  </TableCell>
                ))}
                {/* ITEM-ACTIVE.4b — named, like every other column. It was the
                    one nameless header among seven, which read as an oversight
                    rather than as a column. */}
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length + 1}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length + 1}>
                    <EmptyState icon={InventoryIcon} message={emptyMessage()} />
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => (
                  <TableRow
                    key={item._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(itemBase(item._id))}
                  >
                    {activeColumns.map(col => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        onClick={col.key === 'actions' ? (e => e.stopPropagation()) : undefined}
                        sx={{
                          py: col.key === 'actions' ? 0.75 : 1.25,
                          ...(col.key === 'actions' ? { whiteSpace: 'nowrap' } : {}),
                        }}
                      >
                        {renderCell(col, item)}
                      </TableCell>
                    ))}
                    {/* ITEM-ACTIVE.4 — quiet at rest; the row reveals it on
                        hover, and focus reveals it for the keyboard. */}
                    <TableCell
                      align="right"
                      onClick={e => e.stopPropagation()}
                      sx={{ py: 0.75, whiteSpace: 'nowrap' }}
                    >
                      <ItemRowRetireAction
                        active={item.active !== false}
                        blocked={qtyOf(item) !== 0}
                        quantity={qtyOf(item)}
                        canEdit={isAdmin()}
                        busy={flipBusy}
                        itemName={item.displayName ?? 'this item'}
                        onRetire={() => flipOne(item, false)}
                        onReactivate={() => flipOne(item, true)}
                        onResolveStock={() => setBlockedItem(item)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalItems > pageSize && (
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        )}
      </Paper>

      <QuickReceiveModal
        open={quickReceiveOpen}
        onClose={() => setQuickReceiveOpen(false)}
        onSuccess={loadItems}
        venueId={venueId}
      />

      <RecordWastageModal
        open={wastageOpen}
        onClose={() => setWastageOpen(false)}
        onSuccess={loadItems}
        venueId={venueId}
      />

      <InventoryImportDialog
        open={importOpen}
        venueId={venueId ?? ''}
        onClose={() => setImportOpen(false)}
        onSuccess={loadItems}
      />

      <InventoryModuleHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* ITEM-ACTIVE.4 — the guard answers the same way from the row and from
          the sweep as it does on the item page: with somewhere to go. */}
      {venueId && blockedItem && (
        <ItemDeactivationBlockedDialog
          open
          onClose={() => setBlockedItem(null)}
          venueId={venueId}
          itemId={blockedItem._id}
          displayName={blockedItem.displayName ?? 'This item'}
          quantity={qtyOf(blockedItem)}
        />
      )}

      {undoToast}
    </Box>
  );
};

export default InventoryItemList;
